import { useState } from "react";
import { verifyAudit, type AuditTable } from "../api";

const TABLES: AuditTable[] = ["archive_jobs", "restore_requests", "archive_job_events", "restore_request_events", "retention_restore_requests", "retention_restore_events", "integrity_checks"];

interface Result {
  table: AuditTable;
  ok: boolean;
  rowsChecked: number;
  brokenAtId?: number;
  err?: string;
}

export function Audit() {
  const [results, setResults] = useState<Record<string, Result | "loading">>({});

  async function check(table: AuditTable) {
    setResults(r => ({ ...r, [table]: "loading" }));
    try {
      const r = await verifyAudit(table);
      setResults(prev => ({ ...prev, [table]: r }));
    } catch (e) {
      setResults(prev => ({ ...prev, [table]: { table, ok: false, rowsChecked: 0, err: e instanceof Error ? e.message : String(e) } }));
    }
  }

  async function checkAll() {
    for (const t of TABLES) await check(t);
  }

  return (
    <>
      <div className="row spaced">
        <h2>Audit hash chains</h2>
        <button className="btn" onClick={checkAll}>Verify all</button>
      </div>
      <p className="muted">
        Each row in these tables links to the previous via <code className="mono">row_hash = SHA-256(prev_row_hash || row_canonical)</code>.
        Any silent mutation breaks the chain. ISO 27001 A.8.15 evidence.
      </p>
      <table>
        <thead>
          <tr>
            <th>Table</th>
            <th>Status</th>
            <th>Rows checked</th>
            <th>Broken at</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {TABLES.map(t => {
            const r = results[t];
            return (
              <tr key={t}>
                <td className="mono">{t}</td>
                <td>{render(r)}</td>
                <td>{typeof r === "object" ? r.rowsChecked : ""}</td>
                <td className="mono">{typeof r === "object" && r.brokenAtId != null ? `#${r.brokenAtId}` : ""}</td>
                <td><button className="btn ghost" onClick={() => check(t)}>Verify</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function render(r: Result | "loading" | undefined): JSX.Element {
  if (r === "loading") return <span className="badge amber">checking…</span>;
  if (!r) return <span className="muted">—</span>;
  if (r.err) return <span className="badge red" title={r.err}>error</span>;
  if (r.ok) return <span className="badge green">ok</span>;
  return <span className="badge red">BROKEN</span>;
}
